import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset } from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { ArrowRight, KeyRound, Loader2, CheckCircle2 } from "lucide-react";
import { BRAND } from "@/lib/config";

export default function AdminResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && confirm !== password;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8 || password !== confirm) return;
    setSubmitting(true);
    setError(null);
    try {
      await confirmPasswordReset(token, password);
      setDone(true);
      setTimeout(() => navigate("/admin", { replace: true }), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : "Não foi possível redefinir a senha. O link pode ter expirado."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div
        data-testid="admin-reset-page"
        className="min-h-screen bg-[#f4f1ea] flex items-center justify-center px-6 text-[#1e2a5a]"
      >
        <div className="max-w-md text-center">
          <h1 className="font-display text-3xl">Link inválido</h1>
          <p className="mt-4 text-sm text-[#5c5c5c]">
            Esse link de redefinição não está mais ativo. Solicite um novo.
          </p>
          <Link
            to="/admin/forgot-password"
            className="mt-8 inline-flex items-center gap-2 text-sm link-underline"
          >
            Pedir novo link →
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="admin-reset-page"
      className="min-h-screen bg-[#f4f1ea] text-[#1e2a5a] flex items-center justify-center px-6"
    >
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-12">
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#1e2a5a] text-[#1e2a5a] font-display italic">
            L
          </span>
          <span className="font-display text-lg">{BRAND.name}</span>
        </div>

        <p className="overline">Acesso restrito</p>
        <h1 className="font-display mt-4 text-4xl sm:text-5xl tracking-tight leading-[1.05]">
          Definir nova senha
        </h1>

        {done ? (
          <div
            data-testid="admin-reset-success"
            className="mt-10 bg-white border border-[#1e7a3e]/30 rounded-xl p-6"
          >
            <p className="inline-flex items-center gap-2 font-display text-xl text-[#1e7a3e]">
              <CheckCircle2 className="h-5 w-5" /> Senha atualizada
            </p>
            <p className="mt-3 text-sm text-[#3a3a3a] leading-relaxed">
              Sua nova senha já está ativa. Redirecionando para a página de login…
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="mt-10 space-y-5">
            <label htmlFor="new-password" className="flex flex-col gap-2">
              <span className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] font-medium">
                Nova senha (mínimo 8 caracteres)
              </span>
              <Input
                id="new-password"
                data-testid="admin-reset-password-input"
                type="password"
                required
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
              />
              {tooShort && (
                <span className="text-xs text-[#d9381e]">
                  A senha precisa ter pelo menos 8 caracteres.
                </span>
              )}
            </label>
            <label htmlFor="confirm-password" className="flex flex-col gap-2">
              <span className="text-xs uppercase tracking-[0.18em] text-[#5c5c5c] font-medium">
                Confirmar nova senha
              </span>
              <Input
                id="confirm-password"
                data-testid="admin-reset-confirm-input"
                type="password"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="bg-white border-[#e5e3db] focus-visible:ring-[#1e2a5a]/20 h-11"
              />
              {mismatch && (
                <span className="text-xs text-[#d9381e]">
                  As senhas não coincidem.
                </span>
              )}
            </label>

            {error && (
              <p
                data-testid="admin-reset-error"
                className="text-sm text-[#d9381e]"
              >
                {error}
              </p>
            )}

            <button
              type="submit"
              data-testid="admin-reset-submit"
              disabled={submitting || password.length < 8 || password !== confirm}
              className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1e2a5a] px-6 py-3.5 text-sm font-medium text-[#f4f1ea] transition-colors hover:bg-[#d9381e] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Salvando…
                </>
              ) : (
                <>
                  <KeyRound className="h-4 w-4" /> Salvar nova senha
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
